import { z } from 'zod';
import { extractBindingPaths, isKnownBindingPath } from "./bindings.js";
import { bindingScopeForPageGroup, bindingScopeForSubtree, createRootBindingScope, extendBindingScope, isPathAllowedInScope, mergeBindingScope, scopeForRepeater, } from "./binding-scope.js";
/**
 * Runtime validation for authored templates.
 *
 * The constructor is a rich editor and the DSL is stored as free-form JSON, so
 * this is the boundary that stops a malformed or hand-crafted document from
 * reaching the render pipeline. Backend and constructor share it, which means a
 * template rejected on save is rejected in the editor for the same reason.
 */
const scalarSchema = z.union([z.string(), z.number(), z.boolean(), z.null()]);
export const expressionSchema = z.lazy(() => z.discriminatedUnion('op', [
    z.object({ op: z.literal('const'), value: scalarSchema }),
    z.object({ op: z.literal('var'), path: z.string().min(1) }),
    z.object({ op: z.literal('and'), args: z.array(expressionSchema).min(1) }),
    z.object({ op: z.literal('or'), args: z.array(expressionSchema).min(1) }),
    z.object({ op: z.literal('not'), arg: expressionSchema }),
    z.object({ op: z.literal('eq'), left: expressionSchema, right: expressionSchema }),
    z.object({ op: z.literal('ne'), left: expressionSchema, right: expressionSchema }),
    z.object({ op: z.literal('gt'), left: expressionSchema, right: expressionSchema }),
    z.object({ op: z.literal('gte'), left: expressionSchema, right: expressionSchema }),
    z.object({ op: z.literal('lt'), left: expressionSchema, right: expressionSchema }),
    z.object({ op: z.literal('lte'), left: expressionSchema, right: expressionSchema }),
    z.object({
        op: z.literal('between'),
        value: expressionSchema,
        min: expressionSchema,
        max: expressionSchema,
    }),
    z.object({ op: z.literal('in'), value: expressionSchema, options: z.array(scalarSchema) }),
    z.object({ op: z.literal('exists'), path: z.string().min(1) }),
    z.object({ op: z.literal('empty'), path: z.string().min(1) }),
    z.object({ op: z.literal('length'), path: z.string().min(1) }),
    z.object({ op: z.literal('matches'), path: z.string().min(1), pattern: z.string().max(200) }),
]));
const lengthSchema = z.union([z.number(), z.string().regex(/^\$[\w.-]+$/)]);
const edgeInsetsSchema = z.object({
    top: lengthSchema.optional(),
    right: lengthSchema.optional(),
    bottom: lengthSchema.optional(),
    left: lengthSchema.optional(),
});
const colorValueSchema = z.discriminatedUnion('type', [
    z.object({ type: z.literal('token'), key: z.string().min(1) }),
    z.object({
        type: z.literal('literal'),
        hex: z.string().regex(/^#([0-9a-fA-F]{3,4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/),
    }),
]);
const gradientStopSchema = z.object({
    offset: z.number().min(0).max(1),
    color: colorValueSchema,
});
export const fillSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('solid'), color: colorValueSchema }),
    z.object({
        kind: z.literal('linear'),
        angle: z.number().min(-360).max(360),
        stops: z.array(gradientStopSchema).min(2).max(8),
    }),
    z.object({
        kind: z.literal('radial'),
        center: z.object({ x: z.number().min(0).max(1), y: z.number().min(0).max(1) }),
        radius: z.number().min(0.05).max(2),
        stops: z.array(gradientStopSchema).min(2).max(8),
    }),
    z.object({
        kind: z.literal('image'),
        assetId: z.string().min(1),
        fit: z.enum(['cover', 'contain', 'fill']).optional(),
        opacity: z.number().min(0).max(1).optional(),
    }),
]);
export const effectSchema = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('dropShadow'),
        offsetX: z.number(),
        offsetY: z.number(),
        blur: z.number().min(0),
        spread: z.number(),
        color: colorValueSchema,
        opacity: z.number().min(0).max(1),
    }),
    z.object({
        kind: z.literal('innerShadow'),
        offsetX: z.number(),
        offsetY: z.number(),
        blur: z.number().min(0),
        spread: z.number(),
        color: colorValueSchema,
        opacity: z.number().min(0).max(1),
    }),
    z.object({
        kind: z.literal('blur'),
        radius: z.number().min(0).max(100),
    }),
]);
const boxStyleSchema = z.object({
    background: z.string().optional(),
    fill: fillSchema.optional(),
    effects: z.array(effectSchema).max(8).optional(),
    border: z.string().optional(),
    radius: z.union([z.string(), z.number()]).optional(),
    shadow: z.string().optional(),
    padding: edgeInsetsSchema.optional(),
    margin: edgeInsetsSchema.optional(),
    opacity: z.number().min(0).max(1).optional(),
    rotate: z.number().min(-360).max(360).optional(),
    clip: z.boolean().optional(),
});
const alignSchema = z.enum(['start', 'center', 'end', 'stretch', 'baseline']);
const justifySchema = z.enum(['start', 'center', 'end', 'between', 'around', 'evenly']);
const contentAlignXSchema = z.enum(['start', 'center', 'end', 'stretch']);
const contentAlignYSchema = z.enum(['start', 'center', 'end']);
const layoutPropsSchema = z.object({
    grow: z.number().min(0).optional(),
    shrink: z.number().min(0).optional(),
    basis: lengthSchema.optional(),
    width: z.union([lengthSchema, z.literal('auto'), z.literal('full')]).optional(),
    height: z.union([lengthSchema, z.literal('auto'), z.literal('full')]).optional(),
    minHeight: lengthSchema.optional(),
    maxHeight: lengthSchema.optional(),
    minWidth: lengthSchema.optional(),
    maxWidth: lengthSchema.optional(),
    alignSelf: alignSchema.optional(),
    colSpan: z.number().int().min(1).max(12).optional(),
    rowSpan: z.number().int().min(1).max(12).optional(),
    order: z.number().int().optional(),
});
const nodeFrameSchema = z.object({
    x: z.number().min(-1).max(2),
    y: z.number().min(-1).max(2),
    width: z.number().min(0).max(3),
    height: z.number().min(0).max(3),
    rotate: z.number().min(-360).max(360).optional(),
    z: z.number().int().optional(),
    pinX: z.enum(['left', 'center', 'right', 'stretch']).optional(),
    pinY: z.enum(['top', 'center', 'bottom', 'stretch']).optional(),
});
const nodeBaseShape = {
    id: z.string().min(1),
    name: z.string().optional(),
    style: boxStyleSchema.optional(),
    layout: layoutPropsSchema.optional(),
    frame: nodeFrameSchema.optional(),
    contentAlignX: contentAlignXSchema.optional(),
    contentAlignY: contentAlignYSchema.optional(),
    visibleIf: expressionSchema.optional(),
};
const textSourceSchema = z.object({
    template: z.string().max(2000),
    fallback: z.string().optional(),
    format: z.enum(['none', 'price', 'uppercase', 'lowercase', 'capitalize', 'ordinal']).optional(),
});
const imageSourceSchema = z.object({
    binding: z.string().optional(),
    assetId: z.string().optional(),
    placeholder: z.enum(['none', 'silhouette', 'initials', 'pattern']).optional(),
});
const gridSynthesisSchema = z.object({
    minColumns: z.number().int().min(1).max(8),
    maxColumns: z.number().int().min(1).max(8),
    heroRatio: z.number().min(0).max(1),
    maxHeroSpan: z.number().int().min(1).max(4),
    rhythm: z.enum(['uniform', 'alternating', 'golden', 'organic']),
});
export const nodeSchema = z.lazy(() => z.discriminatedUnion('kind', [
    z.object({
        ...nodeBaseShape,
        kind: z.literal('stack'),
        direction: z.enum(['row', 'column']),
        gap: lengthSchema.optional(),
        align: alignSchema.optional(),
        justify: justifySchema.optional(),
        wrap: z.boolean().optional(),
        children: z.array(nodeSchema),
    }),
    z.object({
        ...nodeBaseShape,
        kind: z.literal('grid'),
        columns: z.number().int().min(1).max(12),
        gap: lengthSchema.optional(),
        columnGap: lengthSchema.optional(),
        rowGap: lengthSchema.optional(),
        rowHeight: z.union([lengthSchema, z.literal('auto')]).optional(),
        synthesize: gridSynthesisSchema.optional(),
        children: z.array(nodeSchema),
    }),
    z.object({
        ...nodeBaseShape,
        kind: z.literal('repeater'),
        source: z.string().min(1),
        as: z.string().min(1),
        limit: z.number().int().min(1).optional(),
        offset: z.number().int().min(0).optional(),
        filter: expressionSchema.optional(),
        sortBy: z.string().optional(),
        sortDirection: z.enum(['asc', 'desc']).optional(),
        itemTemplate: nodeSchema,
        empty: nodeSchema.optional(),
    }),
    z.object({
        ...nodeBaseShape,
        kind: z.literal('slot'),
        slotId: z.string().min(1),
        accepts: z.array(z.enum(['text', 'image', 'shape', 'any'])),
        defaultChild: nodeSchema.optional(),
    }),
    z.object({
        ...nodeBaseShape,
        kind: z.literal('text'),
        source: textSourceSchema,
        typography: z.string().min(1),
        color: z.string().min(1),
        align: z.enum(['left', 'center', 'right', 'justify']).optional(),
        maxLines: z.number().int().min(1).max(20).optional(),
        autoFit: z.boolean().optional(),
    }),
    z.object({
        ...nodeBaseShape,
        kind: z.literal('image'),
        source: imageSourceSchema,
        fit: z.enum(['cover', 'contain', 'fill']),
        aspect: z.number().positive().optional(),
        focus: z.object({ x: z.number().min(0).max(1), y: z.number().min(0).max(1) }).optional(),
        overlay: z.object({ color: z.string(), opacity: z.number().min(0).max(1) }).optional(),
        grayscale: z.boolean().optional(),
    }),
    z.object({
        ...nodeBaseShape,
        kind: z.literal('shape'),
        shape: z.enum(['rect', 'ellipse', 'line']),
        fill: z.union([z.string(), fillSchema]).optional(),
        stroke: z.object({ color: z.string(), width: z.number().min(0) }).optional(),
    }),
    z.object({
        ...nodeBaseShape,
        kind: z.literal('divider'),
        orientation: z.enum(['horizontal', 'vertical']),
        thickness: z.number().min(0).optional(),
        color: z.string().min(1),
        variant: z.enum(['solid', 'dashed', 'dotted', 'leader']).optional(),
    }),
    z.object({
        ...nodeBaseShape,
        kind: z.literal('spacer'),
        size: lengthSchema.optional(),
    }),
    z.object({
        ...nodeBaseShape,
        kind: z.literal('overlay'),
        children: z.array(z.object({
            node: nodeSchema,
            x: z.number().min(-1).max(2),
            y: z.number().min(-1).max(2),
            width: z.number().min(0).max(3),
            height: z.number().min(0).max(3),
            rotate: z.number().optional(),
            z: z.number().int().optional(),
            pinX: z.enum(['left', 'center', 'right', 'stretch']).optional(),
            pinY: z.enum(['top', 'center', 'bottom', 'stretch']).optional(),
        })),
    }),
    z.object({
        ...nodeBaseShape,
        kind: z.literal('group'),
        children: z.array(nodeSchema),
    }),
    z.object({
        ...nodeBaseShape,
        kind: z.literal('frame'),
        entity: z.enum(['wrapper', 'category', 'item']),
        source: z.string().min(1).optional(),
        as: z.string().min(1).optional(),
        limit: z.number().int().min(1).optional(),
        offset: z.number().int().min(0).optional(),
        filter: expressionSchema.optional(),
        children: z.array(nodeSchema),
    }),
]));
const fontSpecSchema = z.object({
    family: z.string().min(1),
    source: z.enum(['google', 'system', 'custom']),
    weights: z.array(z.number().int().min(100).max(900)).min(1),
    italic: z.boolean().optional(),
    fallback: z.string().min(1),
});
const typographyTokenSchema = z.object({
    role: z.enum(['display', 'body', 'accent']),
    size: z.number().positive(),
    weight: z.number().int().min(100).max(900),
    lineHeight: z.number().positive(),
    letterSpacing: z.number().optional(),
    textTransform: z.enum(['none', 'uppercase', 'lowercase', 'capitalize']).optional(),
    italic: z.boolean().optional(),
    minSize: z.number().positive().optional(),
    maxSize: z.number().positive().optional(),
});
const hexColor = z.string().regex(/^#([0-9a-fA-F]{3,4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/);
export const designTokensSchema = z.object({
    colors: z.record(z.string(), hexColor),
    typography: z.record(z.string(), typographyTokenSchema),
    spacing: z.record(z.string(), z.number()),
    radii: z.record(z.string(), z.number()),
    borders: z.record(z.string(), z.object({
        width: z.number().min(0),
        style: z.enum(['solid', 'dashed', 'dotted']),
        color: z.string(),
    })),
    shadows: z.record(z.string(), z.object({
        offsetX: z.number(),
        offsetY: z.number(),
        blur: z.number().min(0),
        spread: z.number(),
        color: z.string(),
        opacity: z.number().min(0).max(1),
    })),
    fonts: z.object({
        display: fontSpecSchema,
        body: fontSpecSchema,
        accent: fontSpecSchema,
    }),
});
const pageGroupSchema = z.enum([
    'cover',
    'backCover',
    'toc',
    'divider',
    'category',
    'spotlight',
    'filler',
    'info',
]);
export const blueprintSchema = z.object({
    id: z.string().min(1),
    group: pageGroupSchema,
    label: z.string().min(1),
    variant: z.string().min(1),
    constraints: z.object({
        minItems: z.number().int().min(0).optional(),
        maxItems: z.number().int().min(0).optional(),
        minImageCoverage: z.number().min(0).max(1).optional(),
        maxImageCoverage: z.number().min(0).max(1).optional(),
        maxDescriptionLength: z.number().int().min(0).optional(),
        maxNameLength: z.number().int().min(0).optional(),
        requiresCategoryImage: z.boolean().optional(),
        side: z.enum(['left', 'right', 'any']).optional(),
        firstPageOfCategoryOnly: z.boolean().optional(),
        allowedWhen: expressionSchema.optional(),
    }),
    weight: z.number().min(0).max(100),
    capacity: z
        .object({
        min: z.number().int().min(1),
        preferred: z.number().int().min(1),
        max: z.number().int().min(1),
    })
        .optional(),
    root: nodeSchema,
});
export const frozenPageSchema = z.object({
    id: z.string().min(1),
    title: z.string().optional(),
    group: pageGroupSchema,
    categoryId: z.string().optional(),
    itemIds: z.array(z.string()).optional(),
    root: nodeSchema,
});
const exportSpecSchema = z.object({
    kinds: z.array(z.enum(['pdf', 'png', 'jpeg', 'html', 'zip'])).min(1),
    defaultKind: z.enum(['pdf', 'png', 'jpeg', 'html', 'zip']),
    output: z.enum(['book', 'page', 'scene']),
    print: z
        .object({
        cropMarks: z.boolean().optional(),
        foldPanels: z.number().int().min(1).max(8).optional(),
        profile: z.enum(['digital', 'print']).optional(),
    })
        .optional(),
});
const formatVariantSchema = z.object({
    id: z.string().min(1),
    format: z.enum([
        'digital_book',
        'single_page',
        'flyer',
        'table_tent',
        'wall_board',
        'tv_slide',
        'interior_sheet',
        'pocket_menu',
        'social_post',
    ]),
    label: z.string().min(1),
    canvas: z.object({
        width: z.number().min(200).max(4000),
        height: z.number().min(200).max(4000),
        spread: z.boolean(),
        bleed: z.number().min(0).max(400),
        safeArea: z.number().min(0).max(400),
    }),
    blueprints: z.array(blueprintSchema).min(1),
    flow: z.object({
        includeCover: z.boolean(),
        includeBackCover: z.boolean(),
        includeTableOfContents: z.boolean(),
        dividers: z.enum(['none', 'perCategory', 'auto']),
        includeInfoPage: z.boolean(),
        varietyWindow: z.number().int().min(0).max(10),
        balanceSpreads: z.boolean(),
        minOrphanItems: z.number().int().min(0).max(10),
        allowSpotlights: z.boolean(),
        maxSpotlights: z.number().int().min(0).max(20),
    }),
    export: exportSpecSchema,
});
export const templateVersionSchema = z.object({
    id: z.string().min(1),
    templateId: z.string().min(1),
    version: z.number().int().min(1),
    status: z.enum(['draft', 'published', 'archived']),
    format: z
        .enum([
        'digital_book',
        'single_page',
        'flyer',
        'table_tent',
        'wall_board',
        'tv_slide',
        'interior_sheet',
        'pocket_menu',
        'social_post',
    ])
        .optional()
        .default('digital_book'),
    canvas: z.object({
        width: z.number().min(200).max(4000),
        height: z.number().min(200).max(4000),
        spread: z.boolean(),
        bleed: z.number().min(0).max(100),
        safeArea: z.number().min(0).max(400),
    }),
    tokens: designTokensSchema,
    brandable: z.object({
        colors: z.array(z.string()),
        fonts: z.array(z.enum(['display', 'body', 'accent'])),
        allowRadiusScale: z.boolean().optional(),
        allowTypeScale: z.boolean().optional(),
    }),
    contrastRules: z.array(z.object({ text: z.string(), background: z.string(), minLc: z.number().min(0).max(110) })),
    mode: z.enum(['template', 'frozen']).optional(),
    /** Empty allowed for frozen restaurant designs (`mode: 'frozen'`). */
    blueprints: z.array(blueprintSchema),
    pages: z.array(frozenPageSchema).optional(),
    flow: z.object({
        includeCover: z.boolean(),
        includeBackCover: z.boolean(),
        includeTableOfContents: z.boolean(),
        dividers: z.enum(['none', 'perCategory', 'auto']),
        includeInfoPage: z.boolean(),
        varietyWindow: z.number().int().min(0).max(10),
        balanceSpreads: z.boolean(),
        minOrphanItems: z.number().int().min(0).max(10),
        allowSpotlights: z.boolean(),
        maxSpotlights: z.number().int().min(0).max(20),
    }),
    assets: z.array(z.object({
        id: z.string().min(1),
        url: z.string().min(1),
        kind: z.enum(['image', 'pattern', 'texture']),
        label: z.string().optional(),
    })),
    libraryPresets: z
        .array(z.object({
        id: z.string().min(1),
        label: z.string().min(1),
        node: nodeSchema,
    }))
        .optional(),
    variants: z.array(formatVariantSchema).min(1).optional(),
});
/**
 * Structural validation plus the referential checks Zod cannot express: every
 * colour, typography and spacing key a node mentions must exist in the token
 * table, otherwise the page renders with silent holes instead of failing loudly.
 */
export function validateTemplateVersion(input) {
    const parsed = templateVersionSchema.safeParse(input);
    if (!parsed.success) {
        return {
            valid: false,
            issues: parsed.error.issues.map((issue) => ({
                path: issue.path.join('.'),
                message: issue.message,
            })),
        };
    }
    const template = parsed.data;
    const issues = [];
    // Zod already requires these maps; defensive defaults keep Object.keys safe
    // if a caller ever bypasses the schema with a partially stripped payload.
    const tokens = template.tokens;
    const colorKeys = new Set(Object.keys(tokens.colors ?? {}));
    const typeKeys = new Set(Object.keys(tokens.typography ?? {}));
    const spacingKeys = new Set(Object.keys(tokens.spacing ?? {}));
    const radiusKeys = new Set(Object.keys(tokens.radii ?? {}));
    const borderKeys = new Set(Object.keys(tokens.borders ?? {}));
    const shadowKeys = new Set(Object.keys(tokens.shadows ?? {}));
    const assetIds = new Set(template.assets.map((asset) => asset.id));
    template.assets.forEach((asset, index) => {
        const isSafeUrl = /^(https?:\/\/|data:image\/)/i.test(asset.url);
        if (!isSafeUrl) {
            issues.push({
                path: `assets[${index}].url`,
                message: 'Asset URL must use http(s) or data:image scheme',
            });
        }
    });
    const variantIds = new Set();
    template.variants?.forEach((variant, index) => {
        if (variant.id.trim() !== variant.id) {
            issues.push({
                path: `variants[${index}].id`,
                message: 'Format variant id must not contain leading or trailing whitespace',
            });
        }
        if (variantIds.has(variant.id)) {
            issues.push({
                path: `variants[${index}].id`,
                message: `Duplicate format variant id "${variant.id}"`,
            });
        }
        variantIds.add(variant.id);
        if (!variant.export.kinds.includes(variant.export.defaultKind)) {
            issues.push({
                path: `variants[${index}].export.defaultKind`,
                message: 'Default export kind must be included in export kinds',
            });
        }
        if (!variant.blueprints.some((blueprint) => blueprint.group === 'category')) {
            issues.push({
                path: `variants[${index}].blueprints`,
                message: 'Each format variant needs at least one "category" blueprint',
            });
        }
        checkCanvasConstraints(variant.canvas, `variants[${index}].canvas`, issues);
    });
    const checkLength = (value, where) => {
        if (typeof value === 'string' && value.startsWith('$') && !spacingKeys.has(value.slice(1))) {
            issues.push({ path: where, message: `Unknown spacing token "${value}"` });
        }
    };
    const checkColorValue = (value, where) => {
        if (!value)
            return;
        if (value.type === 'token' && !colorKeys.has(value.key)) {
            issues.push({ path: where, message: `Unknown colour "${value.key}"` });
        }
    };
    const checkFill = (fill, where) => {
        if (!fill)
            return;
        if (fill.kind === 'solid') {
            checkColorValue(fill.color, `${where}.color`);
            return;
        }
        if (fill.kind === 'image') {
            if (!assetIds.has(fill.assetId)) {
                issues.push({
                    path: `${where}.assetId`,
                    message: `Unknown template asset "${fill.assetId}"`,
                });
            }
            return;
        }
        fill.stops.forEach((stop, index) => {
            checkColorValue(stop.color, `${where}.stops[${index}].color`);
        });
    };
    const checkEffects = (effects, where) => {
        effects?.forEach((effect, index) => {
            if (effect.kind === 'dropShadow' || effect.kind === 'innerShadow') {
                checkColorValue(effect.color, `${where}[${index}].color`);
            }
        });
    };
    const visit = (node, path, scope) => {
        inspectExpression(node.visibleIf, `${path}.visibleIf`, scope, issues);
        const style = node.style;
        if (style) {
            if (style.background && !colorKeys.has(style.background)) {
                issues.push({
                    path: `${path}.style.background`,
                    message: `Unknown colour "${style.background}"`,
                });
            }
            checkFill(style.fill, `${path}.style.fill`);
            checkEffects(style.effects, `${path}.style.effects`);
            if (style.border && !borderKeys.has(style.border)) {
                issues.push({ path: `${path}.style.border`, message: `Unknown border "${style.border}"` });
            }
            if (typeof style.radius === 'string' && !radiusKeys.has(style.radius)) {
                issues.push({ path: `${path}.style.radius`, message: `Unknown radius "${style.radius}"` });
            }
            if (style.shadow && !shadowKeys.has(style.shadow)) {
                issues.push({ path: `${path}.style.shadow`, message: `Unknown shadow "${style.shadow}"` });
            }
            for (const side of ['top', 'right', 'bottom', 'left']) {
                checkLength(style.padding?.[side], `${path}.style.padding.${side}`);
                checkLength(style.margin?.[side], `${path}.style.margin.${side}`);
            }
        }
        switch (node.kind) {
            case 'text':
                if (!typeKeys.has(node.typography)) {
                    issues.push({
                        path: `${path}.typography`,
                        message: `Unknown typography token "${node.typography}"`,
                    });
                }
                if (!colorKeys.has(node.color)) {
                    issues.push({ path: `${path}.color`, message: `Unknown colour "${node.color}"` });
                }
                for (const bindingPath of extractBindingPaths(node.source.template)) {
                    if (!isKnownBindingPath(bindingPath)) {
                        issues.push({
                            path: `${path}.source.template`,
                            message: `Unknown binding path "{{${bindingPath}}}"`,
                        });
                    }
                    else if (bindingPath === 'restaurant.wifiPassword') {
                        issues.push({
                            path: `${path}.source.template`,
                            message: 'Sensitive binding "{{restaurant.wifiPassword}}" is not allowed in public designs',
                        });
                    }
                    else if (!isPathAllowedInScope(bindingPath, scope)) {
                        issues.push({
                            path: `${path}.source.template`,
                            message: `Binding "{{${bindingPath}}}" is not available in this context — wrap content in a matching frame or repeater`,
                        });
                    }
                }
                break;
            case 'image':
                if (node.source.assetId && !assetIds.has(node.source.assetId)) {
                    issues.push({
                        path: `${path}.source.assetId`,
                        message: `Unknown template asset "${node.source.assetId}"`,
                    });
                }
                if (node.source.binding) {
                    if (!isKnownBindingPath(node.source.binding)) {
                        issues.push({
                            path: `${path}.source.binding`,
                            message: `Unknown binding path "${node.source.binding}"`,
                        });
                    }
                    else if (node.source.binding === 'restaurant.wifiPassword') {
                        issues.push({
                            path: `${path}.source.binding`,
                            message: 'Sensitive binding "{{restaurant.wifiPassword}}" is not allowed in public designs',
                        });
                    }
                    else if (!isPathAllowedInScope(node.source.binding, scope)) {
                        issues.push({
                            path: `${path}.source.binding`,
                            message: `Binding "${node.source.binding}" is not available in this context — wrap content in a matching frame or repeater`,
                        });
                    }
                }
                if (node.overlay && !colorKeys.has(node.overlay.color)) {
                    issues.push({
                        path: `${path}.overlay.color`,
                        message: `Unknown colour "${node.overlay.color}"`,
                    });
                }
                break;
            case 'divider':
                if (!colorKeys.has(node.color)) {
                    issues.push({ path: `${path}.color`, message: `Unknown colour "${node.color}"` });
                }
                break;
            case 'shape':
                if (typeof node.fill === 'string' && !colorKeys.has(node.fill)) {
                    issues.push({ path: `${path}.fill`, message: `Unknown colour "${node.fill}"` });
                }
                else if (node.fill && typeof node.fill !== 'string') {
                    checkFill(node.fill, `${path}.fill`);
                }
                break;
            case 'stack':
            case 'grid':
            case 'group':
                node.children.forEach((child, index) => visit(child, `${path}.children[${index}]`, scope));
                break;
            case 'frame':
                {
                    let childScope = extendBindingScope(scope, node.entity);
                    if (node.source) {
                        childScope = mergeBindingScope(childScope, scopeForRepeater(node.as ?? node.entity, node.source));
                    }
                    node.children.forEach((child, index) => visit(child, `${path}.children[${index}]`, childScope));
                }
                break;
            case 'overlay':
                node.children.forEach((child, index) => visit(child.node, `${path}.children[${index}]`, scope));
                break;
            case 'repeater':
                inspectExpression(node.filter, `${path}.filter`, scope, issues);
                visit(node.itemTemplate, `${path}.itemTemplate`, mergeBindingScope(scope, bindingScopeForSubtree(node)));
                if (node.empty)
                    visit(node.empty, `${path}.empty`, scope);
                break;
            case 'slot':
                if (node.defaultChild)
                    visit(node.defaultChild, `${path}.defaultChild`, scope);
                break;
            default:
                break;
        }
    };
    template.blueprints.forEach((blueprint, index) => {
        inspectExpression(blueprint.constraints.allowedWhen, `blueprints[${index}].constraints.allowedWhen`, mergeBindingScope(createRootBindingScope(), bindingScopeForPageGroup(blueprint.group)), issues);
        visit(blueprint.root, `blueprints[${index}].root`, mergeBindingScope(createRootBindingScope(), bindingScopeForPageGroup(blueprint.group)));
        if (blueprint.capacity) {
            const { min, preferred, max } = blueprint.capacity;
            if (min > max || preferred < min || preferred > max) {
                issues.push({
                    path: `blueprints[${index}].capacity`,
                    message: 'Capacity must satisfy min <= preferred <= max',
                });
            }
        }
    });
    template.variants?.forEach((variant, variantIndex) => {
        variant.blueprints.forEach((blueprint, blueprintIndex) => {
            inspectExpression(blueprint.constraints.allowedWhen, `variants[${variantIndex}].blueprints[${blueprintIndex}].constraints.allowedWhen`, mergeBindingScope(createRootBindingScope(), bindingScopeForPageGroup(blueprint.group)), issues);
            visit(blueprint.root, `variants[${variantIndex}].blueprints[${blueprintIndex}].root`, mergeBindingScope(createRootBindingScope(), bindingScopeForPageGroup(blueprint.group)));
            if (blueprint.capacity) {
                const { min, preferred, max } = blueprint.capacity;
                if (min > max || preferred < min || preferred > max) {
                    issues.push({
                        path: `variants[${variantIndex}].blueprints[${blueprintIndex}].capacity`,
                        message: 'Capacity must satisfy min <= preferred <= max',
                    });
                }
            }
        });
    });
    for (const brandableColor of template.brandable.colors) {
        if (!colorKeys.has(brandableColor)) {
            issues.push({
                path: 'brandable.colors',
                message: `Brandable colour "${brandableColor}" is not in the token table`,
            });
        }
    }
    for (const [index, rule] of template.contrastRules.entries()) {
        if (!colorKeys.has(rule.text) || !colorKeys.has(rule.background)) {
            issues.push({
                path: `contrastRules[${index}]`,
                message: 'Contrast rule references a colour that is not in the token table',
            });
        }
    }
    if (template.mode === 'frozen') {
        if (!template.pages?.length) {
            issues.push({ path: 'pages', message: 'Frozen designs need at least one page' });
        }
        template.pages?.forEach((page, index) => {
            visit(page.root, `pages[${index}].root`, mergeBindingScope(createRootBindingScope(), bindingScopeForPageGroup(page.group)));
        });
    }
    else if (!template.blueprints.some((blueprint) => blueprint.group === 'category')) {
        // A book with no way to lay out a category page cannot render at all.
        issues.push({ path: 'blueprints', message: 'At least one "category" blueprint is required' });
    }
    checkCanvasConstraints(template.canvas, 'canvas', issues);
    return { valid: issues.length === 0, issues };
}
function checkCanvasConstraints(canvas, path, issues) {
    if (canvas.bleed * 2 >= Math.min(canvas.width, canvas.height)) {
        issues.push({ path, message: 'Bleed must leave positive usable canvas space' });
    }
    if (canvas.safeArea * 2 >= Math.min(canvas.width, canvas.height)) {
        issues.push({ path, message: 'Safe area must leave positive content space' });
    }
    if (canvas.safeArea + canvas.bleed >= Math.min(canvas.width, canvas.height) / 2) {
        issues.push({ path, message: 'Safe area and bleed consume the canvas' });
    }
}
function inspectExpression(expression, path, scope, issues, depth = 0) {
    if (!expression)
        return;
    if (depth > 20) {
        issues.push({ path, message: 'Expression nesting exceeds the limit of 20' });
        return;
    }
    const expressionPath = 'path' in expression ? expression.path : undefined;
    if (expressionPath && (!isKnownBindingPath(expressionPath) || !isPathAllowedInScope(expressionPath, scope))) {
        issues.push({ path, message: `Binding "${expressionPath}" is not available in this context` });
    }
    if (expression.op === 'matches') {
        try {
            // Compile once at validation time so malformed patterns never reach render.
            new RegExp(expression.pattern);
        }
        catch {
            issues.push({ path, message: 'Expression contains an invalid regular expression' });
        }
    }
    switch (expression.op) {
        case 'and':
        case 'or':
            expression.args.forEach((child) => inspectExpression(child, path, scope, issues, depth + 1));
            break;
        case 'not':
            inspectExpression(expression.arg, path, scope, issues, depth + 1);
            break;
        case 'eq':
        case 'ne':
        case 'gt':
        case 'gte':
        case 'lt':
        case 'lte':
            inspectExpression(expression.left, path, scope, issues, depth + 1);
            inspectExpression(expression.right, path, scope, issues, depth + 1);
            break;
        case 'between':
            inspectExpression(expression.value, path, scope, issues, depth + 1);
            inspectExpression(expression.min, path, scope, issues, depth + 1);
            inspectExpression(expression.max, path, scope, issues, depth + 1);
            break;
        case 'in':
            inspectExpression(expression.value, path, scope, issues, depth + 1);
            break;
        default:
            break;
    }
}
//# sourceMappingURL=schema.js.map